import { useState } from 'react';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import LinkOut from '@spectrum-icons/workflow/LinkOut';

import { AffiliateLink } from '../../../affiliates/components/AffiliateLink';
import { buyTarget, formatPrintDate, printPrices } from './cardInfoModel';
import { Section } from './Section';

// How many printings the section shows before the reader asks for the rest.
// Long-tail staples have well over a hundred.
const PREVIEW_COUNT = 6;

function rarityLabel(rarity) {
    if (!rarity) return null;
    return rarity.charAt(0).toUpperCase() + rarity.slice(1);
}

/**
 * Every printing of the card, cheapest price first on the row, each row a buy
 * link for that exact printing. This is the affiliate surface, so the buy
 * affordance is spelled out rather than implied by a hover state.
 */
export function CardPrintings({ prints, loading, cardName, headingRef }) {
    const [showAll, setShowAll] = useState(false);
    const visible = showAll ? prints : prints.slice(0, PREVIEW_COUNT);
    return (
        <Section
            title="Printings"
            hint="Prices come from Scryfall. Every row opens that printing on TCGPlayer."
            headingRef={headingRef}
        >
            {loading && prints.length === 0 && (
                <ol className="cardInfoPrints" aria-busy="true">
                    {[0, 1, 2, 3].map((row) => (
                        <li className="cardInfoPrint" key={row}>
                            <span className="skeletonBlock cardInfoPrintSkeleton">&nbsp;</span>
                        </li>
                    ))}
                </ol>
            )}
            {!loading && prints.length === 0 && (
                <p className="cardInfoEmpty">Scryfall returned no printings for this card.</p>
            )}
            {prints.length > 0 && (
                <ol className="cardInfoPrints">
                    {visible.map((print) => {
                        const prices = printPrices(print);
                        return (
                            <li className="cardInfoPrint" key={print.id}>
                                <AffiliateLink
                                    provider="tcgplayer"
                                    target={buyTarget(print, cardName)}
                                    placement="printslist"
                                    className="cardInfoPrintLink"
                                    title={`Buy ${
                                        print.setName || 'this printing'
                                    } on TCGPlayer — affiliate link, supports the site`}
                                >
                                    <span className="cardInfoPrintSet">
                                        {(print.set || '').toUpperCase()}
                                        <span className="cardInfoPrintCollector">#{print.collectorNumber}</span>
                                    </span>
                                    <span className="cardInfoPrintMeta">
                                        {[rarityLabel(print.rarity), formatPrintDate(print.releasedAt)]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </span>
                                    <span className="cardInfoPrintPrices">
                                        {prices.length === 0 ? (
                                            <span className="cardInfoPrintPrice cardInfoPrintPrice--none">
                                                No price
                                            </span>
                                        ) : (
                                            prices.map((price) => (
                                                <span
                                                    className={`cardInfoPrintPrice cardInfoPrintPrice--${price.key}`}
                                                    key={price.key}
                                                >
                                                    {price.label && (
                                                        <span className="cardInfoPrintFinish">{price.label}</span>
                                                    )}
                                                    {price.value}
                                                </span>
                                            ))
                                        )}
                                    </span>
                                    <span className="cardInfoPrintBuy">
                                        Buy
                                        <LinkOut size="XS" />
                                    </span>
                                </AffiliateLink>
                            </li>
                        );
                    })}
                </ol>
            )}
            {prints.length > PREVIEW_COUNT && (
                <button
                    type="button"
                    className="buttonReset cardInfoMore"
                    aria-expanded={showAll}
                    onClick={() => setShowAll((open) => !open)}
                >
                    {showAll ? 'Show fewer printings' : `Show all ${prints.length} printings`}
                    <ChevronDown size="XS" />
                </button>
            )}
        </Section>
    );
}
